/**
 * TRAD-3701 — page_view 를 «그 화면의 제목이 정해진 뒤»에 한 번 보내는 순수 스케줄러(타이머는 주입).
 *
 * 규칙:
 *  - navigate(path, location) — 새 화면. 대기 중이던 이전 화면은 즉시 보낸다(flush).
 *  - replace(path, location) — 주소만 교체(리다이렉트 · 진입 보정). 아직 안 보낸 화면은 사용자가 본 적 없는 주소라 버리고 새 주소로 대기한다.
 *    이미 보낸 뒤면 navigate 와 같다(필터 탭처럼 사용자가 만든 replace 는 종전대로 1건).
 *  - titlePublished(path, title) — 현재 대기 화면과 «같은 경로»의 제목만 받는다. 받은 뒤 settleMs 동안 더 오면 마지막 값.
 *    (상세처럼 데이터가 와서 제목이 한 번 더 바뀌는 화면 대비)
 *  - 제목이 maxWaitMs 안에 안 오면 page_title 없이 보낸다 — 🔴 «이전 화면 제목»으로 채우지 않는다(그게 이 티켓의 결함).
 *  - page_location 은 navigate 시점의 값을 그대로 보낸다(GA 의 dl 이 이전 URL 로 남는 것 방지).
 */
export interface PageViewHit {
  path: string;
  title: string | undefined;
  location: string;
}

export interface PageViewSchedulerDeps {
  send: (hit: PageViewHit) => void;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
  /** 구독 전에 이미 알려진 제목(등록소의 마지막 값). */
  getLastPublished?: () => { path: string; title: string } | null;
  settleMs?: number;
  maxWaitMs?: number;
}

export interface PageViewScheduler {
  navigate: (path: string, location: string) => void;
  replace: (path: string, location: string) => void;
  titlePublished: (path: string, title: string) => void;
  flush: () => void;
}

export const PAGE_VIEW_SETTLE_MS = 300;
export const PAGE_VIEW_MAX_WAIT_MS = 2000;

export const createPageViewScheduler = ({
  send,
  setTimer,
  clearTimer,
  getLastPublished,
  settleMs = PAGE_VIEW_SETTLE_MS,
  maxWaitMs = PAGE_VIEW_MAX_WAIT_MS,
}: PageViewSchedulerDeps): PageViewScheduler => {
  let pending: PageViewHit | null = null;
  let settleTimer: unknown = null;
  let maxTimer: unknown = null;

  const clearTimers = () => {
    if (settleTimer !== null) clearTimer(settleTimer);
    if (maxTimer !== null) clearTimer(maxTimer);
    settleTimer = null;
    maxTimer = null;
  };

  const flush = () => {
    clearTimers();
    if (!pending) return;
    const hit = pending;
    pending = null;
    send(hit);
  };

  const titlePublished = (path: string, title: string) => {
    if (!pending || pending.path !== path) return;
    pending = { ...pending, title };
    if (settleTimer !== null) clearTimer(settleTimer);
    settleTimer = setTimer(flush, settleMs);
  };

  const navigate = (path: string, location: string) => {
    flush();
    pending = { path, title: undefined, location };
    maxTimer = setTimer(flush, maxWaitMs);
    const known = getLastPublished?.();
    if (known && known.path === path) titlePublished(path, known.title);
  };

  const replace = (path: string, location: string) => {
    pending = null; // 타이머는 navigate 의 flush 가 지운다.
    navigate(path, location);
  };

  return { navigate, replace, titlePublished, flush };
};
